import { formatUnits } from '@ethersproject/units';
import ferroLogo from '@public/images/ferro-logo.png';
import maverickLogo from '@public/images/maverick-text-logo.png';
import singleLogo from '@public/images/single-text-logo.png';
import syncswapLogo from '@public/images/syncswap-text-logo.png';
import tectonicLogo from '@public/images/tectonic-logo.png';
import ferroImage from '@public/images/use-ferro.png';
import maverickImage from '@public/images/use-maverick.png';
import singleImage from '@public/images/use-single.png';
import syncswapImage from '@public/images/use-syncswap.svg';
import tectonicImage from '@public/images/use-tectonic.png';
import vvsImage from '@public/images/use-vvs.png';
import vvsLogo from '@public/images/vvs-logo.png';
import { useQuery } from '@tanstack/react-query';
import type { CoinMode } from '@ui/hooks/useCoinMode';
import { COIN_MODE, useGlobalCoinMode } from '@ui/hooks/useCoinMode';
import useGardenApy from '@ui/hooks/useGardenApy';
import {
  useTectonicApy,
  useTectonicSupplyUsd,
} from '@ui/hooks/usePartnerStats';
import { useTranslations } from '@ui/i18n';
import { getQuery } from '@ui/queries/queries';
import { QueryKey } from '@ui/queries/queryKey';
import { formatNumber, formatPercentage } from '@ui/utils/format';
import type { GardenType } from '@ui/utils/garden';
import {
  formatGardenApy,
  gardenTokenDict,
  getGardenName,
} from '@ui/utils/garden';
import type { SupplyLcroModalType } from '@ui/views/UseLcroDirectly/types';
import type { StaticImageData } from 'next/image';

import { useSingleFinanceLcroFarmInfo } from './useSingleFinanceLcroFarmInfo';

export const useUseLcroPartnerCardsData = (_coinMode?: CoinMode) => {
  const globalCoinMode = useGlobalCoinMode();
  const coinMode = _coinMode ?? globalCoinMode;

  const ferroPartnerCard = useFerroPartnerCard({
    coinMode,
    hidden: coinMode === 'eth' || coinMode === 'tia',
  });

  const tectonicPartnerCard = useTectonicPartnerCard({
    coinMode,
    hidden: coinMode === 'eth' || coinMode === 'tia',
  });

  const vvsCroPartnerCard = useVvsPartnerCard('VVS_CRO', {
    coinMode,
    comingSoon: !gardenTokenDict[coinMode].VVS_CRO.enabled,
    hidden: coinMode !== 'atom',
  });

  const vvsTiaPartnerCard = useVvsPartnerCard('VVS_TIA', {
    coinMode,
    comingSoon: !gardenTokenDict[coinMode].VVS_TIA.enabled,
    hidden: coinMode !== 'tia',
  });

  const singleFinancePartnerCard = useSingleFinancePartnerCard({
    coinMode,
    hidden: coinMode !== 'cro',
  });

  const syncswapPartnerCard = useSyncSwapPartnerCard({
    coinMode,
    hidden: coinMode !== 'eth',
    comingSoon: false,
  });

  const maverickPartnerCard = useMaverickPartnerCard({
    coinMode,
    hidden: coinMode !== 'eth',
    comingSoon: false,
  });

  const partnerCardData: UseLcroDirectlyPartnerCardProps[] = [
    ferroPartnerCard,
    tectonicPartnerCard,
    vvsCroPartnerCard,
    singleFinancePartnerCard,
    syncswapPartnerCard,
    maverickPartnerCard,
    vvsTiaPartnerCard,
  ].filter((v): v is UseLcroDirectlyPartnerCardProps => Boolean(v));

  return { partnerCardData };
};

export type UseLcroDirectlyPartnerCardProps = {
  image: StaticImageData;
  logo: StaticImageData;
  description: string;
  buttonText: string;
  logoWidth: number;
  logoHeight?: number;
  tvl: string | null;
  apy?: string | null;
  type: SupplyLcroModalType | 'SINGLE' | 'SYNCSWAP' | 'Maverick';
  comingSoon?: boolean;
};

type UsePartnerCardOption = {
  coinMode: CoinMode;
  comingSoon?: boolean;
  hidden?: boolean;
};

const useFerroPartnerCard = ({
  coinMode,
  comingSoon,
  hidden,
}: UsePartnerCardOption): UseLcroDirectlyPartnerCardProps | null => {
  const t = useTranslations();
  const coinModeInfo = COIN_MODE[coinMode];
  const shouldFetchData = !comingSoon && !hidden;

  const { ferroGardenApy } = useGardenApy(
    'FERRO',
    true,
    coinMode,
    shouldFetchData,
  );

  const ferroTvlQuery = getQuery(QueryKey.GET_FERRO_TOTAL_LOCKED_VALUE)({
    coinMode,
  });
  const { data: ferroTvl, error: ferroTvlError } = useQuery(
    ferroTvlQuery.queryKey,
    ferroTvlQuery.queryFn,
    {
      enabled: shouldFetchData,
    },
  );

  return hidden
    ? null
    : {
        comingSoon,
        logoWidth: 88,
        image: ferroImage,
        logo: ferroLogo,
        description: t(
          "Provide liquidity for {liquidCoin} on Cronos' leading stable swap and earn additional rewards on your {liquidCoin} Earn even more with the {garden}",
          {
            liquidCoin: coinModeInfo.liquidName,
            garden: getGardenName(coinMode, 'FERRO'),
          },
        ),
        buttonText: t('Supply {coin} on {platform}', {
          coin: coinModeInfo.liquidName,
          platform: 'Ferro',
        }),
        tvl: ferroTvlError
          ? null
          : ferroTvl
          ? formatNumber(ferroTvl, 2, true, true)
          : '',
        apy: ferroGardenApy === 0 ? null : formatGardenApy(ferroGardenApy),
        type: 'FERRO',
      };
};

const useTectonicPartnerCard = ({
  coinMode,
  comingSoon,
  hidden,
}: UsePartnerCardOption): UseLcroDirectlyPartnerCardProps | null => {
  const t = useTranslations();
  const coinModeInfo = COIN_MODE[coinMode];
  const shouldFetchData = !comingSoon && !hidden;

  const { tectonicGardenApy } = useGardenApy(
    'TECTONIC',
    true,
    coinMode,
    shouldFetchData,
  );

  const tectonicApyRet = useTectonicApy(shouldFetchData);

  const { data: tectonicSupplyUsd, error: tectonicTvlError } =
    useTectonicSupplyUsd({
      enabled: shouldFetchData,
    });

  const tectonicSupply = tectonicSupplyUsd
    ? parseFloat(formatUnits(tectonicSupplyUsd ?? 0, 18))
    : undefined;

  return hidden
    ? null
    : {
        comingSoon,
        logoWidth: 140,
        image: tectonicImage,
        logo: tectonicLogo,
        description: !gardenTokenDict[coinMode].TECTONIC.enabled
          ? t('use-latom-tectonic-desc')
          : t(
              'Supply {liquidCoin} on the leading money market on Cronos and earn interest from borrowers Earn additional rewards with the {garden}',
              {
                liquidCoin: coinModeInfo.liquidName,
                garden: getGardenName(coinMode, 'TECTONIC'),
              },
            ),
        buttonText: t('Supply {coin} on {platform}', {
          coin: coinModeInfo.liquidName,
          platform: 'Tectonic',
        }),
        tvl: tectonicTvlError
          ? null
          : tectonicSupply !== undefined
          ? formatNumber(tectonicSupply, 2, true, true)
          : '',
        apy: (() => {
          if (!gardenTokenDict[coinMode].TECTONIC.enabled) {
            if (tectonicApyRet.error || tectonicApyRet.data === 0) {
              return null;
            }
            return typeof tectonicApyRet.data !== 'undefined'
              ? formatGardenApy(tectonicApyRet.data / 100)
              : undefined;
          }

          return tectonicGardenApy === 0
            ? null
            : formatGardenApy(tectonicGardenApy);
        })(),
        type: 'TECTONIC',
      };
};

const useVvsPartnerCard = (
  gardenType: GardenType,
  { coinMode, comingSoon, hidden }: UsePartnerCardOption,
): UseLcroDirectlyPartnerCardProps | null => {
  const t = useTranslations();
  const coinModeInfo = COIN_MODE[coinMode];
  const shouldFetchData = !comingSoon && !hidden;
  const { vvsTiaGardenApy } = useGardenApy(
    gardenType,
    true,
    coinMode,
    shouldFetchData,
  );

  return hidden
    ? null
    : {
        comingSoon,
        logoWidth: 140,
        image: vvsImage,
        logo: vvsLogo,
        description: t(
          'Put your {liquidCoin} to work on VVS Finance to earn attractive yield farming rewards!',
          {
            liquidCoin: coinModeInfo.liquidName,
          },
        ),
        buttonText: t('Supply {coin} on {platform}', {
          coin: coinModeInfo.liquidName,
          platform: 'VVS',
        }),
        tvl: null,
        apy: vvsTiaGardenApy === 0 ? null : formatGardenApy(vvsTiaGardenApy),
        type: gardenType,
      };
};

const useSingleFinancePartnerCard = ({
  coinMode,
  comingSoon,
  hidden,
}: UsePartnerCardOption): UseLcroDirectlyPartnerCardProps | null => {
  const t = useTranslations();
  const coinModeInfo = COIN_MODE[coinMode];
  const shouldFetchData = !comingSoon && !hidden;

  const singleFinanceFarmTvlRet = useSingleFinanceLcroFarmInfo({
    enabled: shouldFetchData,
  });

  return hidden
    ? null
    : {
        logoWidth: 57,
        logoHeight: 30,
        image: singleImage,
        logo: singleLogo,
        description: t(
          `Use {liquidCoin} for leveraged yield farming on Single Finance`,
          {
            liquidCoin: coinModeInfo.liquidName,
          },
        ),
        buttonText: t('Supply {coin} on {platform}', {
          coin: coinModeInfo.liquidName,
          platform: 'Single',
        }),
        tvl: singleFinanceFarmTvlRet.data?.tvlInUSD
          ? `$${formatNumber(singleFinanceFarmTvlRet.data.tvlInUSD, 2)}`
          : null,
        apy: singleFinanceFarmTvlRet.data?.apy
          ? formatPercentage(singleFinanceFarmTvlRet.data.apy)
          : null,
        type: 'SINGLE',
      };
};

const useSyncSwapPartnerCard = ({
  coinMode,
  comingSoon,
  hidden,
}: UsePartnerCardOption): UseLcroDirectlyPartnerCardProps | null => {
  const t = useTranslations();
  const coinModeInfo = COIN_MODE[coinMode];

  return hidden
    ? null
    : {
        logoWidth: 121,
        logoHeight: 29,
        image: syncswapImage,
        logo: syncswapLogo,
        comingSoon,
        description: t(`syncswap-description`),
        buttonText: t('Supply {coin} on {platform}', {
          coin: coinModeInfo.liquidName,
          platform: 'Syncswap',
        }),
        tvl: null,
        apy: null,
        type: 'SYNCSWAP',
      };
};

const useMaverickPartnerCard = ({
  coinMode,
  comingSoon,
  hidden,
}: UsePartnerCardOption): UseLcroDirectlyPartnerCardProps | null => {
  const t = useTranslations();
  const coinModeInfo = COIN_MODE[coinMode];

  return hidden
    ? null
    : {
        logoWidth: 150,
        logoHeight: 40,
        image: maverickImage,
        logo: maverickLogo,
        comingSoon,
        description: t('maverick-description'),
        buttonText: t('Supply {coin} on {platform}', {
          coin: coinModeInfo.liquidName,
          platform: 'Maverick',
        }),
        tvl: null,
        apy: null,
        type: 'Maverick',
      };
};
